/** Company work types that can be turned on or off in Settings → Company. */
export const ALL_COMPANY_STAGES = ['MOT', 'Bodyshop', 'Workshop', 'Tyres', 'Sales'];
export const GARAGE_LANE_STAGES = ['MOT', 'Bodyshop', 'Workshop', 'Tyres'];

const STAGE_SET = new Set(ALL_COMPANY_STAGES);
const IN_PROGRESS_STATUSES = ['In Progress', 'Awaiting Parts'];

/** Missing or empty config means every work type is on (existing companies). */
export const normalizeEnabledStages = (raw) => {
  if (!Array.isArray(raw) || raw.length === 0) return [...ALL_COMPANY_STAGES];
  const next = ALL_COMPANY_STAGES.filter((stage) => raw.includes(stage) && STAGE_SET.has(stage));
  return next.length ? next : [...ALL_COMPANY_STAGES];
};

export const getEnabledStages = (garageInfo) => normalizeEnabledStages(garageInfo?.enabledStages);

export const isStageEnabled = (garageInfo, stage) => getEnabledStages(garageInfo).includes(stage);

/** Work-type stage for a vehicle (MOT, Sales, etc.), or null if not tied to a work type. */
export const getVehicleWorkStage = (vehicle) => {
  const status = vehicle?.status || '';
  if (ALL_COMPANY_STAGES.includes(status)) return status;
  if (IN_PROGRESS_STATUSES.includes(status)) {
    const previous = vehicle?.previousStatus || 'Workshop';
    return ALL_COMPANY_STAGES.includes(previous) ? previous : 'Workshop';
  }
  return null;
};

/** Whether a vehicle should appear in views gated by company work types. */
export const isVehicleWorkTypeVisible = (garageInfo, vehicle) => {
  const stage = getVehicleWorkStage(vehicle);
  if (!stage) return false;
  return isStageEnabled(garageInfo, stage);
};

export const filterVehiclesByWorkTypes = (vehicles, garageInfo) =>
  (vehicles || []).filter((vehicle) => isVehicleWorkTypeVisible(garageInfo, vehicle));

/** True when at least one garage lane (MOT/Bodyshop/Workshop/Tyres) is enabled. */
export const hasGarageLanesEnabled = (garageInfo) =>
  GARAGE_LANE_STAGES.some((stage) => isStageEnabled(garageInfo, stage));

export const getDefaultBookingStage = (garageInfo) => {
  const enabled = getEnabledStages(garageInfo);
  const preferred = ['Workshop', 'MOT', 'Tyres', 'Bodyshop', 'Sales'];
  return preferred.find((stage) => enabled.includes(stage)) || enabled[0] || 'Workshop';
};

/** Stage dropdown options: enabled types, plus the current value so existing jobs stay editable. */
export const getStageSelectOptions = (garageInfo, currentStatus) => {
  const enabled = getEnabledStages(garageInfo);
  if (currentStatus && STAGE_SET.has(currentStatus) && !enabled.includes(currentStatus)) {
    return [...enabled, currentStatus];
  }
  return enabled;
};
